import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  IconButton, TextField, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Alert, Snackbar, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, Card, CardContent,
  alpha, useTheme, Stack, Skeleton, Grid, FormControlLabel, Checkbox, Switch, Autocomplete,
} from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useNavigate } from 'react-router-dom';
import { getAreaStyle, getAreaIcon } from '../../components/documents/areaConfig';
import * as flowService from '../../services/documentFlowService';
import type { DocumentFlow } from '../../services/documentFlowService';

const STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'default'; icon: React.ReactElement }> = {
  active: { label: 'Ativo', color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  draft: { label: 'Rascunho', color: 'warning', icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
  inactive: { label: 'Inativo', color: 'default', icon: <PauseCircleIcon sx={{ fontSize: 14 }} /> },
};

const AREAS = [
  { value: '', label: 'Todas as Areas' },
  { value: 'civel', label: 'Civel' }, { value: 'trabalhista', label: 'Trabalhista' },
  { value: 'criminal', label: 'Criminal' }, { value: 'tributario', label: 'Tributario' },
  { value: 'administrativo', label: 'Administrativo' }, { value: 'previdenciario', label: 'Previdenciario' },
  { value: 'familia', label: 'Familia' }, { value: 'empresarial', label: 'Empresarial' },
];

const CATEGORIES = [
  { value: '', label: 'Todas as Categorias' },
  { value: 'pecas_juridicas', label: 'Peças Jurídicas' }, { value: 'contratos', label: 'Contratos' },
  { value: 'extrajudicial', label: 'Extrajudicial' }, { value: 'ferramentas', label: 'Ferramentas' },
];

const DocumentFlowsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [flows, setFlows] = useState<DocumentFlow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; flow: DocumentFlow | null }>({ open: false, flow: null });
  const [mergeDialog, setMergeDialog] = useState<{ flow: DocumentFlow | null; target: DocumentFlow | null; options: DocumentFlow[] }>({ flow: null, target: null, options: [] });
  const [mergeSearch, setMergeSearch] = useState('');

  const loadFlows = async () => {
    setLoading(true); setError(null);
    try {
      const result = await flowService.listFlows({
        search: search || undefined, area: filterArea || undefined,
        category: filterCategory || undefined, status: filterStatus || undefined,
        show_disabled: showDisabled, only_approved: onlyApproved,
        skip: page * rowsPerPage, limit: rowsPerPage,
      });
      setFlows(result.flows); setTotal(result.total);
    } catch { setError('Erro ao carregar fluxos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadFlows(); }, [page, rowsPerPage, filterArea, filterCategory, filterStatus, showDisabled, onlyApproved]);
  const handleSearch = () => { setPage(0); loadFlows(); };

  // Opções do "mesclar em": fluxos ativos com nome parecido
  useEffect(() => {
    const source = mergeDialog.flow;
    if (!source) return;
    // sugestão inicial: primeira palavra significativa do nome ("consignação", "Manifestação"), que acha as variantes
    const generic = ['ação', 'acao', 'petição', 'peticao', 'peça', 'peca', 'pedido', 'para', 'com', 'sobre'];
    const keyword = source.name.replace(/\(.*?\)/g, ' ').split(/[\s/,–-]+/)
      .find(w => w.length >= 4 && !generic.includes(w.toLowerCase())) || source.name;
    const term = mergeSearch || keyword;
    const timer = setTimeout(() => {
      flowService.listFlows({ search: term, limit: 30 })
        .then(r => setMergeDialog(d => ({ ...d, options: r.flows.filter(f => f.id !== source.id && !f.merged_into) })))
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [mergeDialog.flow, mergeSearch]);

  const handleMerge = async () => {
    const { flow, target } = mergeDialog;
    if (!flow || !target) return;
    try {
      const r = await flowService.mergeFlow(flow.id, target.id);
      setSnackbar({ open: true, message: `"${flow.name}" mesclado em "${target.name}"; ${r.drafts_moved} rascunho(s) movido(s)`, severity: 'success' });
      setMergeDialog({ flow: null, target: null, options: [] });
      loadFlows();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Não foi possível mesclar', severity: 'error' });
    }
  };

  const handleUnmerge = async (flow: DocumentFlow) => {
    try {
      const r = await flowService.unmergeFlow(flow.id);
      setSnackbar({ open: true, message: `Mescla desfeita; ${r.drafts_returned} rascunho(s) devolvido(s)`, severity: 'success' });
      loadFlows();
    } catch {
      setSnackbar({ open: true, message: 'Não foi possível desfazer a mescla', severity: 'error' });
    }
  };

  const handleDuplicate = async (flow: DocumentFlow) => {
    try { await flowService.duplicateFlow(flow.id); setSnackbar({ open: true, message: 'Fluxo duplicado', severity: 'success' }); loadFlows(); }
    catch { setSnackbar({ open: true, message: 'Erro ao duplicar', severity: 'error' }); }
  };

  const handleDelete = async () => {
    if (!deleteDialog.flow) return;
    try { await flowService.deleteFlow(deleteDialog.flow.id); setSnackbar({ open: true, message: 'Fluxo desativado', severity: 'success' }); setDeleteDialog({ open: false, flow: null }); loadFlows(); }
    catch { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleStatusToggle = async (flow: DocumentFlow) => {
    const ns = flow.status === 'active' ? 'inactive' : 'active';
    try { await flowService.updateFlowStatus(flow.id, ns); setSnackbar({ open: true, message: `Status: ${STATUS_CONFIG[ns]?.label}`, severity: 'success' }); loadFlows(); }
    catch { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleApprovalToggle = async (flow: DocumentFlow) => {
    const newApproved = !flow.is_approved;
    try { 
      await flowService.updateFlowApproval(flow.id, newApproved); 
      setSnackbar({ open: true, message: newApproved ? 'Fluxo aprovado' : 'Aprovacao removida', severity: 'success' }); 
      loadFlows(); 
    }
    catch { setSnackbar({ open: true, message: 'Erro ao alterar aprovacao', severity: 'error' }); }
  };

  const getFlowAreas = (flow: DocumentFlow): string[] => {
    if (flow.areas && flow.areas.length > 0) return flow.areas;
    if (flow.area) return [flow.area];
    return [];
  };

  const stats = {
    total: total,
    active: flows.filter(f => f.status === 'active').length,
    totalUses: flows.reduce((sum, f) => sum + (f.usage_count || 0), 0),
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Fluxos de Documentos</Typography>
          <Typography variant="body2" color="text.secondary">Gerencie os fluxos de geracao de documentos com IA</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/document-flows/new')}
          sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}>
          Novo Fluxo
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total de Fluxos', value: stats.total, icon: <AccountTreeIcon />, color: theme.palette.primary.main },
          { label: 'Fluxos Ativos', value: stats.active, icon: <CheckCircleIcon />, color: theme.palette.success.main },
          { label: 'Total de Usos', value: stats.totalUses, icon: <TrendingUpIcon />, color: theme.palette.info.main },
        ].map((s, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(s.color, 0.1), color: s.color }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Buscar por nome..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Area</InputLabel>
            <Select value={filterArea} label="Area" sx={{ borderRadius: 2 }}
              onChange={(e) => { setFilterArea(e.target.value); setPage(0); }}>
              {AREAS.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Categoria</InputLabel>
            <Select value={filterCategory} label="Categoria" sx={{ borderRadius: 2 }}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(0); }}>
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" sx={{ borderRadius: 2 }}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="draft">Rascunho</MenuItem>
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadFlows} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDisabled}
                onChange={(e) => { setShowDisabled(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={<Typography variant="body2">Mostrar fluxos desabilitados</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={onlyApproved}
                onChange={(e) => { setOnlyApproved(e.target.checked); setPage(0); }}
                size="small"
                color="success"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VerifiedIcon sx={{ fontSize: 16, color: onlyApproved ? 'success.main' : 'text.disabled' }} />
                <Typography variant="body2">Apenas aprovados por advogados</Typography>
              </Box>
            }
          />
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>{[1,2,3,4].map(i => <Skeleton key={i} height={56} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Fluxo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Area</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Versao</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Usos</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Modo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                      <AccountTreeIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                      <Typography color="text.secondary" fontWeight={500}>Nenhum fluxo encontrado</Typography>
                    </TableCell>
                  </TableRow>
                ) : flows.map((flow) => {
                  const st = STATUS_CONFIG[flow.status] || STATUS_CONFIG['draft'];
                  const flowAreas = getFlowAreas(flow);
                  return (
                    <TableRow key={flow.id} hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                      onClick={() => navigate(`/admin/document-flows/${flow.id}`)}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(getAreaStyle(flowAreas[0] || 'civel').color, 0.1) }}>
                            {getAreaIcon(flowAreas[0] || 'civel', 22)}
                          </Box>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" fontWeight={700}>{flow.name}</Typography>
                              {flow.merged_into && (
                                <Chip label="Mesclado" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                              )}
                              {flow.is_approved && (
                                <Tooltip title="Aprovado pelo time de advogados">
                                  <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{flow.slug}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2">{flow.category?.replace(/_/g, ' ')}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {flowAreas.map((area, idx) => (
                            <Chip key={idx} label={getAreaStyle(area).label} size="small"
                              sx={{ fontWeight: 600, borderRadius: 1.5, bgcolor: alpha(getAreaStyle(area).color, 0.1), color: getAreaStyle(area).color }} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip icon={st.icon} label={st.label} color={st.color} size="small" sx={{ fontWeight: 600, borderRadius: 1.5 }}
                          onClick={(e) => { e.stopPropagation(); handleStatusToggle(flow); }} />
                      </TableCell>
                      <TableCell align="center"><Chip label={`v${flow.version}`} size="small" variant="outlined" sx={{ minWidth: 36, fontWeight: 600, borderRadius: 1.5 }} /></TableCell>
                      <TableCell align="center"><Typography variant="body2" fontWeight={600}>{flow.usage_count}</Typography></TableCell>
                      <TableCell align="center">
                        <Chip label={flow.generation_mode === 'advanced' ? 'Avancado' : 'Simples'} size="small" variant="outlined"
                          color={flow.generation_mode === 'advanced' ? 'primary' : 'default'} sx={{ borderRadius: 1.5, fontWeight: 500 }} />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={flow.is_approved ? "Remover aprovacao" : "Aprovar fluxo"}>
                            <IconButton size="small" onClick={() => handleApprovalToggle(flow)}
                              sx={{ 
                                '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) },
                                color: flow.is_approved ? 'success.main' : 'text.disabled'
                              }}>
                              <VerifiedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => navigate(`/admin/document-flows/${flow.id}`)}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          {flow.merged_into ? (
                            <Tooltip title="Desfazer mescla"><IconButton size="small" onClick={() => handleUnmerge(flow)}><UndoIcon fontSize="small" /></IconButton></Tooltip>
                          ) : (
                            <Tooltip title="Mesclar em outro fluxo (duplicata)"><IconButton size="small"
                              onClick={() => { setMergeSearch(''); setMergeDialog({ flow, target: null, options: [] }); }}><CallMergeIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          <Tooltip title="Duplicar"><IconButton size="small" onClick={() => handleDuplicate(flow)}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.08) } }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Desativar"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, flow })}
                            sx={{ '&:hover': { bgcolor: alpha('#f44336', 0.08), color: 'error.main' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]} labelRowsPerPage="Por pagina"
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          </>
        )}
      </Paper>

      {/* Merge Dialog */}
      <Dialog open={!!mergeDialog.flow} onClose={() => setMergeDialog({ flow: null, target: null, options: [] })} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CallMergeIcon color="primary" /> Mesclar duplicata</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>"{mergeDialog.flow?.name}"</strong> sai do catálogo. Quem abrir o link dele cai no fluxo escolhido abaixo, e
            os rascunhos em andamento vão para lá com os dados preenchidos. Pode ser desfeito depois.
          </Typography>
          <Autocomplete
            options={mergeDialog.options}
            value={mergeDialog.target}
            onChange={(_e, v) => setMergeDialog(d => ({ ...d, target: v }))}
            onInputChange={(_e, v, reason) => { if (reason === 'input') setMergeSearch(v); }}
            getOptionLabel={(f) => `${f.name} (${f.slug}) · ${f.usage_count ?? 0} usos`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Fluxo que fica" placeholder="Buscar pelo nome" />}
            noOptionsText="Nenhum fluxo ativo com esse nome"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMergeDialog({ flow: null, target: null, options: [] })} sx={{ borderRadius: 2 }}>Cancelar</Button>
          <Button onClick={handleMerge} variant="contained" disabled={!mergeDialog.target} sx={{ borderRadius: 2 }}>Mesclar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, flow: null })} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutlineIcon color="error" /> Desativar Fluxo
        </DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja desativar <strong>"{deleteDialog.flow?.name}"</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            O fluxo não será excluído permanentemente, apenas ficará inativo para os usuários.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, flow: null })} sx={{ borderRadius: 2 }}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ borderRadius: 2 }}>Desativar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentFlowsPage;
